import { useMemo, useState } from 'react';
import { Typography, useTheme } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Props as LegendProps } from 'recharts/types/component/Legend';

import { GoalProgressChartData, Retailer_Network_Performance_Period } from '@occc/graphql';
import { DateTimeFormat, formatDateTime } from '@occc-app/lib/date';

import {
  getParsedChartData,
  getXInterval,
  getYDomainValues,
  getYTickValues,
} from './retailer-network-performance-chart.utils';
import { CustomTooltip } from './custom-tooltip.component';
import { CustomLegend } from './custom-legend.component';
import { ChartName } from './retailer-network-performance-chart.types';

import { noDataMessage } from '../../../../retailer-connect.copies';
import { formatPerformanceNumber } from '../../retailer-network-performance.utils';

type ChartProps = {
  chartData: GoalProgressChartData[];
  targetGoal: number;
  period: Retailer_Network_Performance_Period;
  chartName: ChartName;
};

export const Chart = ({ chartData, targetGoal, period, chartName }: ChartProps) => {
  const theme = useTheme();
  const [barIndexInFocus, setBarIndexInFocus] = useState<number | null>(null);
  const [goalToDate, setGoalToDate] = useState<number | null>(null);

  const axisLineColor = theme.palette.default.main;
  const barFill = theme.palette.info.main;
  const barDarkFill = theme.palette.info.dark;
  const highlightColor = theme.palette.error.main;

  // Parsed chart data
  const { data, highestEntryValue } = useMemo(
    () => getParsedChartData(chartData, period, targetGoal),
    [chartData, period, targetGoal],
  );

  // Get final y-domain range, taking into account room needed for the tooltip
  const [yDomain, yDomainUnitSize] = useMemo(
    () => getYDomainValues(targetGoal, highestEntryValue),
    [targetGoal, highestEntryValue],
  );

  // The array of y-ticks based on the increment scale and domain ceiling
  const yTickValues = useMemo(
    () => getYTickValues(yDomain, yDomainUnitSize),
    [yDomain, yDomainUnitSize],
  );

  // Calculates the correct interval for x-ticks
  const xInterval = useMemo(() => getXInterval(period), [period]);

  // Count of characters of the longest y-tick value to determine the width of the y-axis
  const yTickLongestName = formatPerformanceNumber(targetGoal).length;
  const yAxisWidth = yTickLongestName > 3 ? 35 : 20;

  const tick = { fontSize: '10px', fill: theme.palette.text.secondary };

  if (!data) {
    return (
      <Typography my={2} textAlign='center' color='textSecondary'>
        {noDataMessage}
      </Typography>
    );
  }

  return (
    <ResponsiveContainer width='100%' height={180}>
      <BarChart
        data={data}
        margin={{ top: 16, right: 0, left: 8, bottom: 8 }}
        onMouseMove={(state) => {
          const progressForDate = state.activePayload?.[0]?.payload.progressForDate;

          if (state.isTooltipActive && progressForDate) {
            setBarIndexInFocus(state.activeTooltipIndex ?? null);
            setGoalToDate(state.activePayload?.[0]?.payload.goalForDate ?? null);
          } else {
            setBarIndexInFocus(null);
            setGoalToDate(null);
          }
        }}
      >
        <CartesianGrid vertical={false} />

        <XAxis
          height={20}
          dataKey='date'
          tickFormatter={(value: Date) => formatDateTime(value, DateTimeFormat.Date)}
          stroke={axisLineColor}
          tickLine={false}
          tick={tick}
          interval={xInterval}
        />

        <YAxis
          width={yAxisWidth}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: number) => formatPerformanceNumber(value)}
          tick={tick}
          ticks={yTickValues}
          domain={[0, yDomain]}
          allowDecimals={false}
          interval={0}
        />

        <Bar
          dataKey='totalToDate'
          stackId='a'
          name='Total New'
          fill={barFill}
          barSize={8}
          isAnimationActive={false}
        >
          {data.map((_entry, index) => (
            <Cell
              opacity={index === barIndexInFocus || barIndexInFocus === null ? 1 : 0.5}
              key={`cell-${index}`}
            />
          ))}
        </Bar>

        <Bar
          dataKey='dailyNew'
          stackId='a'
          name='Daily New'
          fill={barDarkFill}
          barSize={8}
          radius={[2, 2, 0, 0]}
          isAnimationActive={false}
        >
          {data.map((_entry, index) => (
            <Cell
              opacity={index === barIndexInFocus || barIndexInFocus === null ? 1 : 0.5}
              key={`cell-${index}`}
            />
          ))}
        </Bar>

        {goalToDate && barIndexInFocus !== null && (
          <ReferenceLine
            y={goalToDate}
            stroke={highlightColor}
            label={{
              position:
                barIndexInFocus < data.length / 2 ? 'insideBottomRight' : 'insideBottomLeft',
              value: `${formatPerformanceNumber(goalToDate)} GOAL TO DATE`,
              fill: highlightColor,
              fontSize: 8,
              fontWeight: 600,
            }}
          />
        )}

        <Tooltip
          content={<CustomTooltip chartName={chartName} />}
          cursor={false}
          allowEscapeViewBox={{ y: true }}
        />

        <Legend iconSize={10} iconType='square' content={CustomLegend as LegendProps['content']} />
      </BarChart>
    </ResponsiveContainer>
  );
};
